import { isEqual } from "lodash";
import { insertElement } from "../../common/helpers/Utils";
import { CollisionMode } from "../components/SelectCollisionMode";

/**
 * Appends incoming tags to the grid, resolving Tag ID collisions according to the collision mode.
 * Each tag is checked against the grid as it grows, so duplicates within `incoming` collide too.
 * Returns new arrays and leaves the inputs untouched.
 * @param {object} grid
 * @param {object[]} grid.editedTags Rows as shown in the grid.
 * @param {object[]} grid.tags Saved baseline for each row, index-aligned with `editedTags`.
 * @param {object[]} grid.incoming Tags to add.
 * @param {string} grid.collisionMode One of `CollisionMode`.
 * @returns {{ editedTags: object[], tags: object[] }}
 */
export const mergeIncomingTags = ({
  editedTags,
  tags,
  incoming,
  collisionMode,
}) => {
  const nextEditedTags = [...editedTags];
  const nextTags = [...tags];

  for (const tag of incoming) {
    const collisionIndex = nextEditedTags.findIndex(
      (t) =>
        t.TagIdentifier &&
        tag.TagIdentifier &&
        t.TagIdentifier.toString() === tag.TagIdentifier.toString()
    );
    if (collisionIndex === -1 || collisionMode === CollisionMode.Ignore) {
      insertElement(nextEditedTags, tag, nextEditedTags.length);
      insertElement(nextTags, tag, nextTags.length);
    } else if (collisionMode === CollisionMode.Overwrite) {
      nextEditedTags.splice(collisionIndex, 1, tag);
      nextTags.splice(collisionIndex, 1, tag);
    }
    // Skip drops the colliding tag, and so does NoneSet: callers prompt for a mode first.
  }

  return { editedTags: nextEditedTags, tags: nextTags };
};

/**
 * Marks each edited row dirty when it differs from its saved baseline, and clean when it matches
 * it again. Returns a new set and leaves the input untouched.
 * @param {object} grid
 * @param {Set<number>} grid.dirtyTags Indexes of rows that differ from their saved baseline.
 * @param {object[]} grid.editedTags Rows as shown in the grid.
 * @param {object[]} grid.tags Saved baseline for each row, index-aligned with `editedTags`.
 * @param {Iterable<number>} grid.indices Indexes of the rows that were just edited.
 * @returns {Set<number>}
 */
export const updateDirtyTags = ({ dirtyTags, editedTags, tags, indices }) => {
  const nextDirtyTags = new Set(dirtyTags);
  for (const index of indices) {
    if (isEqual(editedTags[index], tags[index])) {
      nextDirtyTags.delete(index);
    } else {
      nextDirtyTags.add(index);
    }
  }
  return nextDirtyTags;
};
